import {useCallback, useEffect, useRef, useState} from 'react';
import {notificationAPI} from '../services/api';
import {FluxSse} from '../services/fluxSse';
import {NotificationApplicative} from '../types';

/**
 * Alertes applicatives de l'utilisateur courant.
 *
 * La liste est chargée une fois au montage puis mise à jour par le flux SSE — pas de polling,
 * donc aucune requête tant qu'il ne se passe rien.
 */
export const useAlertes = () => {
    const [alertes, setAlertes] = useState<NotificationApplicative[]>([]);
    const [connecte, setConnecte] = useState(false);
    const fluxRef = useRef<FluxSse | null>(null);
    const dejaOuvertRef = useRef(false);

    const recharger = useCallback(async () => {
        try {
            const {data} = await notificationAPI.lister();
            setAlertes(data);
        } catch {
            // Une erreur de chargement ne doit pas casser l'affichage : la cloche reste vide.
        }
    }, []);

    useEffect(() => {
        recharger();

        const flux = notificationAPI.ouvrirFlux({
            evenements: {
                connecte: () => setConnecte(true),
                notification: (event) => {
                    try {
                        const alerte: NotificationApplicative = JSON.parse(event.data);
                        setAlertes(precedentes =>
                            precedentes.some(a => a.id === alerte.id) ? precedentes : [alerte, ...precedentes]);
                    } catch {
                        recharger();
                    }
                },
            },
            onOuvert: () => {
                setConnecte(true);
                // Sur une RÉouverture seulement : les alertes émises pendant la coupure n'ont
                // été poussées à personne, il faut les rattraper. Au premier montage, le
                // recharger() ci-dessus s'en est déjà chargé.
                if (dejaOuvertRef.current) {
                    recharger();
                }
                dejaOuvertRef.current = true;
            },
            onInterrompu: () => setConnecte(false),
            onAbandon: () => setConnecte(false),
        });
        fluxRef.current = flux;

        return () => {
            flux.fermer();
            fluxRef.current = null;
        };
    }, [recharger]);

    const marquerLu = useCallback(async (id: number) => {
        setAlertes(precedentes => precedentes.map(a => (a.id === id ? {...a, lu: true} : a)));
        try {
            await notificationAPI.marquerLu(id);
        } catch {
            recharger();
        }
    }, [recharger]);

    const marquerToutLu = useCallback(async () => {
        setAlertes(precedentes => precedentes.map(a => ({...a, lu: true})));
        try {
            await notificationAPI.marquerToutLu();
        } catch {
            recharger();
        }
    }, [recharger]);

    return {
        alertes,
        nonLues: alertes.filter(a => !a.lu).length,
        connecte,
        marquerLu,
        marquerToutLu,
        recharger,
    };
};
